import React, { useState, useEffect, useCallback } from 'react';
import { HashRouter, Routes, Route, useLocation, Link } from 'react-router-dom';
import Navbar from './components/Navbar.tsx';
import Footer from './components/Footer.tsx';
import ChatWidget from './components/ChatWidget.tsx';
import Home from './pages/Home.tsx';
import Listings from './pages/Listings.tsx';
import PropertyDetails from './pages/PropertyDetails.tsx';
import Admin from './pages/Admin.tsx';
import Communities from './pages/Communities.tsx';
import Guides from './pages/Guides.tsx';
import Careers from './pages/Careers.tsx';
import OffPlan from './pages/OffPlan.tsx';
import UKInvestment from './pages/UKInvestment.tsx';
import { INITIAL_PROPERTIES, DEFAULT_LOGO, INITIAL_AGENTS } from './constants.ts';
import { Property } from './types.ts';
import { getSupabase } from './lib/supabase.ts';
import { LayoutDashboard, ExternalLink, Settings } from 'lucide-react';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const AdminBar = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const location = useLocation();

  const checkAdmin = () => {
    const session = localStorage.getItem('guardian_admin_session');
    setIsAdmin(!!session);
  };

  useEffect(() => {
    checkAdmin();
    window.addEventListener('guardian-login-state-changed', checkAdmin);
    return () => window.removeEventListener('guardian-login-state-changed', checkAdmin);
  }, []);

  if (!isAdmin) return null;

  const isPanel = location.pathname.includes('/admin');

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-slate-900 text-white z-[9999] py-2 px-6 flex justify-between items-center border-t border-gold-500/30 backdrop-blur-md shadow-2xl">
      <div className="flex items-center gap-4">
         <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gold-400 animate-pulse">
            <Settings size={12} /> Admin Mode Active
         </span>
      </div>
      <div className="flex items-center gap-4">
        {isPanel ? (
          <Link to="/" className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded transition-all">
            <ExternalLink size={12}/> Visit Live Site
          </Link>
        ) : (
          <Link to="/admin" className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest bg-gold-500 hover:bg-gold-600 px-3 py-1.5 rounded transition-all">
            <LayoutDashboard size={12}/> Admin Dashboard
          </Link>
        )}
      </div>
    </div>
  );
};

const MortgageCalculator = () => {
  const [price, setPrice] = useState(1500000);
  const [downPayment, setDownPayment] = useState(300000);
  const [interest, setInterest] = useState(4.5);
  const [years, setYears] = useState(25);
  const principal = price - downPayment;
  const monthlyInterest = interest / 100 / 12;
  const numPayments = years * 12;
  const monthlyPayment = (principal * monthlyInterest * Math.pow(1 + monthlyInterest, numPayments)) / (Math.pow(1 + monthlyInterest, numPayments) - 1);

  return (
    <div className="pt-32 pb-20 container mx-auto px-6 max-w-4xl">
       <h1 className="text-4xl font-serif text-slate-900 mb-8 text-center">UAE Mortgage Calculator</h1>
       <div className="bg-white p-8 rounded shadow-lg border border-gray-100 grid md:grid-cols-2 gap-12">
          <div className="space-y-6">
             <div><label className="block text-sm font-bold text-gray-700 mb-1">Property Price (AED)</label><input type="number" value={price} onChange={e=>setPrice(Number(e.target.value))} className="w-full border p-3 rounded focus:border-gold-500 outline-none"/></div>
             <div><label className="block text-sm font-bold text-gray-700 mb-1">Down Payment (AED)</label><input type="number" value={downPayment} onChange={e=>setDownPayment(Number(e.target.value))} className="w-full border p-3 rounded focus:border-gold-500 outline-none"/></div>
             <div><label className="block text-sm font-bold text-gray-700 mb-1">Interest Rate (%)</label><input type="number" step="0.1" value={interest} onChange={e=>setInterest(Number(e.target.value))} className="w-full border p-3 rounded focus:border-gold-500 outline-none"/></div>
             <div><label className="block text-sm font-bold text-gray-700 mb-1">Loan Period (Years)</label><input type="number" value={years} onChange={e=>setYears(Number(e.target.value))} className="w-full border p-3 rounded focus:border-gold-500 outline-none"/></div>
          </div>
          <div className="bg-slate-900 text-white p-8 rounded flex flex-col justify-center text-center">
             <span className="text-gold-400 text-sm uppercase tracking-widest">Monthly Payment</span>
             <span className="text-5xl font-serif font-bold my-4">AED {monthlyPayment.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
             <p className="text-xs text-gray-400 mt-4 italic font-sans">Estimated monthly commitment based on UAE market standard interest rates.</p>
          </div>
       </div>
    </div>
  );
};

const App: React.FC = () => {
  const [properties, setProperties] = useState<Property[]>(INITIAL_PROPERTIES);
  const [logoUrl, setLogoUrl] = useState<string>(DEFAULT_LOGO);

  const fetchData = useCallback(async () => {
    const supabase = getSupabase();
    if (!supabase) {
        setProperties(INITIAL_PROPERTIES);
        return;
    }
    
    try {
        const { data: dbAgents } = await supabase.from('agents').select('*');
        const { data: dbProps } = await supabase.from('properties').select('*');
        
        const mergedAgents = [...INITIAL_AGENTS];
        if (dbAgents) {
            dbAgents.forEach((da: any) => {
                const foundIdx = mergedAgents.findIndex(m => m.id === da.id || m.email.toLowerCase() === da.email.toLowerCase());
                const mappedAgent = { ...da, photoUrl: da.photo_url || da.photoUrl };
                if (foundIdx > -1) mergedAgents[foundIdx] = mappedAgent;
                else mergedAgents.push(mappedAgent);
            });
        }

        if (dbProps && dbProps.length > 0) {
           const mapped = dbProps.map((p:any) => ({ 
             ...p, 
             imageUrl: p.image_url, 
             isFeatured: p.is_featured, 
             agentId: p.agent_id, 
             lat: p.lat, 
             lng: p.lng, 
             agent: mergedAgents.find((a:any) => a.id === p.agent_id) || mergedAgents[0] 
           }));
           setProperties(mapped);
        } else {
           setProperties(INITIAL_PROPERTIES);
        }
        
        const { data: settings } = await supabase.from('site_settings').select('*').eq('id', 'logo_url').maybeSingle();
        if (settings?.setting_value) setLogoUrl(settings.setting_value);
    } catch (e) {
        console.error("Critical Data Fetch Error:", e);
        setProperties(INITIAL_PROPERTIES);
    }
  }, []);

  useEffect(() => {
    fetchData();
    window.addEventListener('supabase-config-updated', fetchData);
    return () => window.removeEventListener('supabase-config-updated', fetchData);
  }, [fetchData]);

  return (
    <HashRouter>
      <ScrollToTop />
      <div className="flex flex-col min-h-screen bg-slate-50 font-sans text-slate-800">
        <Navbar logoUrl={logoUrl} />
        <AdminBar />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<Home featuredProperties={properties.filter(p => p.isFeatured || p.type === 'sale')} />} />
            <Route path="/buy" element={<Listings type="sale" properties={properties} />} />
            <Route path="/rent" element={<Listings type="rent" properties={properties} />} />
            <Route path="/property/:id" element={<PropertyDetails />} />
            <Route path="/off-plan" element={<OffPlan />} />
            <Route path="/uk-investment" element={<UKInvestment />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/communities" element={<Communities />} />
            <Route path="/buying-guide" element={<Guides />} />
            <Route path="/selling-guide" element={<Guides />} />
            <Route path="/mortgage-calculator" element={<MortgageCalculator />} />
            <Route path="/admin" element={ <Admin onUpdateLogo={setLogoUrl} onResetLogo={() => setLogoUrl(DEFAULT_LOGO)} currentLogoUrl={logoUrl} /> } />
          </Routes>
        </main>
        <ChatWidget />
        <Footer />
      </div>
    </HashRouter>
  );
};

export default App;